"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SUBPART_L_NOT_ADOPTED = void 0;
exports.getEpisodicRelief = getEpisodicRelief;
exports.episodicEventsApplicableForState = episodicEventsApplicableForState;
const states_1 = require("encamp-shared/dist/constants/states");
const facilityState_1 = require("../facilityState");
/**
 * States whose programs do not grant the carve-out. Episodic generation is
 * RELIEF added by the 2016 Generator Improvements Rule, and a less-stringent
 * federal provision does not take effect in an authorized state until that
 * state adopts it (40 CFR 271.1(i)(2)) — so non-adoption is a real, per-state
 * fact rather than a quirk of two jurisdictions.
 *
 * The default this list implies — adopted everywhere it does not name — is
 * asserted by nobody yet and is the standing question for the waste SME. Kept
 * as one legible list precisely so the answer changes one line whichever way
 * it goes. See `EPISODIC_EXPECTATIONS`, which is the reviewable artifact.
 */
exports.SUBPART_L_NOT_ADOPTED = new Set([
    "CA",
    "CT",
    "MN",
]);
/**
 * The two facts the answer rests on, kept apart because they are unrelated:
 * a per-state ADOPTION fact, and a FEDERAL TIER rule identical everywhere.
 * Bundling them is what made this look like a property of a jurisdiction's
 * clock, which it is not.
 */
function getEpisodicRelief(query) {
    const state = (0, facilityState_1.parseFacilityState)(query.state);
    if (state !== null && exports.SUBPART_L_NOT_ADOPTED.has(state)) {
        // Spelled out, not abbreviated: this reaches the user as the create
        // form's warning verbatim, where "CA has not adopted…" reads like a code
        // leak.
        const stateName = states_1.stateNamesByAbbreviation.get(state) ?? state;
        return {
            isAvailable: false,
            reason: `${stateName} hasn't adopted the episodic event provision (40 CFR 262 subpart L), ` +
                "so the event will be recorded, but container deadlines won't change",
        };
    }
    if (query.generatorStatus === "LQG") {
        // Verbatim user-facing copy, reached unchanged through the validator: an
        // LQG isn't just "ineligible" the way a non-adopting state is — the
        // facility can still get real value from logging the event (its own
        // record), so the message says that instead of implying the log is a
        // formality with no purpose.
        return {
            isAvailable: false,
            reason: "This facility is a Large Quantity Generator (LQG). Episodic events only protect VSQGs " +
                "and SQGs from being reclassified into a higher generator category, but LQG is already " +
                "the highest. You can still log this event for your own internal records (e.g., incident " +
                "history or audit trail), but it will have no regulatory effect here.",
        };
    }
    // Null status included: declaring an event is itself a subpart L act, and
    // withholding the window would report no deadline where the law imposes one.
    return { isAvailable: true };
}
/**
 * Whether subpart L reaches a facility's STATE at all, independent of
 * generator status — the question the Waste Facility page's episodic tile
 * needs to decide whether it's worth showing at all. Deliberately narrower
 * than `getEpisodicRelief`: an LQG facility still gets the tile (declaring is
 * still legal there, just non-operative — see `getEpisodicRelief`'s LQG
 * branch), but a facility in a state that never adopted subpart L never has
 * anything for the tile to show. `null` state answers federally: applicable.
 */
function episodicEventsApplicableForState(state) {
    const parsed = (0, facilityState_1.parseFacilityState)(state);
    return parsed === null || !exports.SUBPART_L_NOT_ADOPTED.has(parsed);
}
//# sourceMappingURL=relief.js.map